import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { forwardRef, type ElementRef, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { useFieldControl } from './field';

export interface SelectOption<V extends string = string> {
  readonly value: V;
  readonly label: ReactNode;
  /** Plain-text label for type-ahead when `label` is not a string. */
  readonly textValue?: string;
  readonly disabled?: boolean;
}

export interface SelectProps<V extends string = string> {
  readonly value?: V;
  readonly defaultValue?: V;
  readonly onValueChange?: (value: V) => void;
  readonly options: readonly SelectOption<V>[];
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly id?: string;
  readonly name?: string;
  readonly className?: string;
  readonly 'aria-label'?: string;
}

/** Single-select dropdown (Radix). For long or searchable lists use `Combobox`. */
export const Select = forwardRef<ElementRef<typeof SelectPrimitive.Trigger>, SelectProps>(
  (props, ref) => {
    const { value, defaultValue, onValueChange, options, placeholder = 'Choose…', disabled, id, name, className, ...aria } = props;
    const field = useFieldControl();
    // passing `value` (even undefined) means controlled: '' shows the placeholder without React's uncontrolled→controlled warning
    const controlled = 'value' in props;
    return (
      <SelectPrimitive.Root value={controlled ? value ?? '' : undefined} defaultValue={defaultValue} onValueChange={onValueChange} disabled={disabled} name={name}>
        <SelectPrimitive.Trigger
          ref={ref}
          id={id ?? field?.id}
          aria-describedby={field?.describedBy}
          aria-invalid={field?.invalid || undefined}
          aria-label={aria['aria-label']}
          className={cn(
            'flex h-10 w-full items-center justify-between gap-2 rounded-md border border-border-strong/70 bg-surface px-3 text-left text-sm text-text shadow-sm coarse:h-11',
            'focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-60',
            'data-[placeholder]:text-text-muted aria-[invalid=true]:border-negative',
            className,
          )}
        >
          {/* long labels truncate on one line instead of wrapping inside the 40px trigger */}
          <span className="min-w-0 flex-1 truncate">
            <SelectPrimitive.Value placeholder={placeholder} />
          </span>
          <SelectPrimitive.Icon>
            <ChevronDown className="size-4 shrink-0 text-text-muted" aria-hidden />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position="popper"
            sideOffset={4}
            className="z-50 max-h-[min(var(--radix-select-content-available-height),320px)] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-lg border border-border bg-surface-raised shadow-lg data-[state=open]:animate-scale-in"
          >
            <SelectPrimitive.Viewport className="p-1">
              {options.map((o) => (
                <SelectPrimitive.Item
                  key={o.value}
                  value={o.value}
                  disabled={o.disabled}
                  textValue={o.textValue ?? (typeof o.label === 'string' ? o.label : o.value)}
                  className="relative flex min-h-10 cursor-pointer select-none items-center rounded-md py-2 pl-8 pr-3 text-sm text-text outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-surface-sunken data-[disabled]:opacity-50"
                >
                  <SelectPrimitive.ItemIndicator className="absolute left-2.5 inline-flex items-center">
                    <Check className="size-4 text-primary" aria-hidden />
                  </SelectPrimitive.ItemIndicator>
                  <SelectPrimitive.ItemText>{o.label}</SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    );
  },
);
Select.displayName = 'Select';
